<template>
  <div class="guide-zapier">
    <p class="guide-lead">
      The eSign Launchpad Zapier integration connects signing events to 8,000+
      apps in Zapier's catalog without writing code. Trigger Salesforce updates
      when a contract completes, push signed PDFs to Google Drive, alert your
      team in Slack when a signer fails KBA - all from the same OAuth-authorized
      connection. The integration ships 10 triggers, 7 actions, and 3 searches
      covering the full package lifecycle, share-link delivery, and embedded
      signing.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What is Zapier?</h2>
    <p>
      <a href="https://zapier.com" target="_blank" rel="noopener">Zapier</a> is
      a no-code automation platform. A "Zap" is a workflow that starts with a
      <strong>trigger</strong> from one app and runs one or more
      <strong>actions</strong> in other apps. Triggers can be webhook-driven
      (instant) or schedule-driven (poll-based). Actions include sending email,
      uploading files, creating records in a CRM, posting to Slack, and
      thousands more.
    </p>
    <p>
      The eSign Launchpad app on Zapier is a Private integration today -
      browse-and-install will go live with a future App Directory listing.
      Contact support for an invite to access the Private app.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Prerequisites</h2>
    <ul>
      <li>
        A <strong>Zapier account</strong>. Free, Starter, Professional, or
        higher all work - the eSign Launchpad triggers are instant (webhook-
        driven), so they don't consume Zapier's poll quota.
      </li>
      <li>
        An <strong>eSign Launchpad portal account</strong> with permission for
        the resources you intend to automate. Connection happens via OAuth, so
        the Zap inherits the connecting user's role and tenant. A read-only
        user can subscribe to triggers and run search actions; only Senders +
        higher can run create-style actions like <em>Send Package</em>,
        <em>Void Package</em>, or <em>Send a Download Link</em>.
      </li>
      <li>
        The <strong>Business tier subscription</strong> (or higher) on your
        eSign Launchpad tenant. Zapier traffic counts as API access, which is
        a Business-tier feature, and webhooks are gated to Business+ as well.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Connecting Zapier to your account</h2>
    <p>
      The first time you add an eSign Launchpad trigger or action to a Zap,
      Zapier prompts for authentication. The flow is standard OAuth 2.0 with
      PKCE:
    </p>
    <ol>
      <li>Click <strong>Sign in to eSign Launchpad</strong> in the Zap editor.</li>
      <li>
        Zapier redirects you to <code>www.esignlaunchpad.com/oauth/consent</code>
        with the requested scope set displayed.
      </li>
      <li>
        Review the scopes - each one is described in plain language, e.g.
        <em>"View your signing packages - read package details, signers,
        status, and signed PDFs."</em>
      </li>
      <li>
        Click <strong>Allow</strong>. Zapier receives an authorization code
        and exchanges it for an access token + refresh token.
      </li>
      <li>
        The connection appears in
        <a href="https://zapier.com/app/connections" target="_blank" rel="noopener">Zapier &rarr; Connections</a>
        and can be reused across any Zap.
      </li>
    </ol>
    <p>
      Refresh tokens rotate on every refresh. If the integration adds new
      scopes in a future version (e.g. v3.1.0 added <code>sign.package.void</code>
      for the Void Package action), Zapier will prompt for re-authentication
      on first run of any Zap using the new capability - your old connection
      keeps working for the existing scope set.
    </p>
    <p>
      You can revoke the integration at any time from
      <a href="/Settings/ConnectedApps">Settings &rarr; Connected Apps</a> on
      eSign Launchpad. Disconnect there immediately invalidates Zapier's access
      and refresh tokens; running Zaps will start failing on next event delivery
      and prompt re-authorization.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Triggers</h2>
    <p>
      Triggers are events that start a Zap. All eSign Launchpad triggers are
      <strong>instant</strong> (webhook-driven, sub-second latency, no poll
      quota consumption).
    </p>

    <h3>Package lifecycle triggers</h3>
    <ul>
      <li><strong>Package Sent</strong> - fires when a package is dispatched for signing.</li>
      <li><strong>Package Completed</strong> - every signer has finished and the signed PDFs are available.</li>
      <li><strong>Package Declined</strong> - any signer declined; no further signing can occur.</li>
      <li><strong>Package Voided</strong> - sender voided/cancelled an in-flight package.</li>
    </ul>

    <h3>Per-signer triggers</h3>
    <p>
      Per-signer events fire once per signer. A 5-signer package sending to
      every signer simultaneously will fire <em>Signer Viewed</em> up to 5
      times. Use these for per-contact CRM updates without waiting for the
      whole package to complete.
    </p>
    <ul>
      <li><strong>Signer Viewed Package</strong> - engagement signal that confirms notifications landed in the inbox.</li>
      <li><strong>Signer Completed</strong> - an individual signer finished their portion.</li>
      <li><strong>Signer Authenticated</strong> - signer passed an authentication challenge (Email OTP, SMS OTP, KBA, ID Verification). Per-signer + per-method.</li>
      <li><strong>Signer Authentication Failed</strong> - auth attempt failed. Carries an attempt counter and an <code>isTerminal</code> flag (signer locked out).</li>
    </ul>

    <h3>Share-link triggers</h3>
    <p>
      The <em>Send a Download Link</em> action mints recipient-email-gated
      share URLs. These triggers close the loop on whether the recipient
      actually received the artifact - a signal no other eSignature Zapier
      integration exposes.
    </p>
    <ul>
      <li><strong>Share Link Accessed</strong> - recipient clicked through to the landing page (before the OTP challenge). Lighter engagement signal.</li>
      <li><strong>Share Link Redeemed</strong> - recipient OTP'd through and downloaded the artifact.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Actions</h2>

    <h3>Send Package From Template</h3>
    <p>
      Pick one of your saved templates from a dynamic dropdown. The action
      reads the template's role schema and renders Name + Email fields per
      role automatically. One step - creates and sends in a single action.
    </p>

    <h3>Send a Download Link</h3>
    <p>
      Mint a recipient-email-gated download URL for a signed document, the
      full package (ZIP), the evidence pack, or the certificate of completion.
      The Zap pipes the URL into Gmail / Outlook / Slack / a CRM note -
      recipient enters a one-time code from their email before the file
      becomes available.
    </p>
    <p>
      <strong>Recommended for distributing artifacts</strong> - see "Choosing
      the right action" below for why this beats attaching the file directly
      in most cases.
    </p>

    <h3>Download Signed Documents (ZIP)</h3>
    <p>
      Fetch all signed documents for a completed package as a ZIP, ready to
      pipe into Google Drive / Dropbox / SharePoint / S3. The Zapier file
      stash holds the bytes for the duration of the Zap run.
    </p>

    <h3>Void Package</h3>
    <p>
      Terminate an in-flight package (Sent / Viewed / In Progress). Signers
      who haven't completed are notified via email. Common error-handling-
      branch action - for example, "if the deal stage changes to Closed Lost
      after we've already sent, void the package."
    </p>

    <h3>Resend Notification to Signer</h3>
    <p>
      Re-send the signing-notification email to one specific pending signer.
      Useful for chasing a holdout without prompting every signer on the
      package. Server-side rate-limited to 5 resends per signer per minute.
    </p>

    <h3>Get URL for Embedded Signer</h3>
    <p>
      Generate a single-use, time-limited signing URL for a signer that was
      created with a Client User ID (the embedded-signing pattern). Drop the
      URL into Slack, an iframe, a CRM note. Standard email-link signers will
      return an error here - for those, use <em>Resend Notification to
      Signer</em> instead.
    </p>

    <h3>Download Audit Trail</h3>
    <p>
      Fetch the package's full audit trail (every signer view, signature, auth
      challenge, system event, cryptographic chain hash) as PDF, JSON, or CSV.
      Optional IP-address redaction for counterparty deliveries that fall
      under privacy regulations forbidding IP exchange.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Searches</h2>
    <p>
      Searches look up existing data without modifying it. The canonical use
      is "trigger fires from another app, search resolves the matching eSign
      Launchpad record, then act on it."
    </p>

    <h3>Find Package</h3>
    <p>
      Look up packages by external reference id, status, or search term.
      Returns up to 10 matches sorted by most-recently-updated. Refuses to
      run with no filter - an unfiltered list-all is almost never what you
      want and would silently pick the wrong package.
    </p>

    <h3>Get Package by ID</h3>
    <p>
      Direct GUID lookup. Returns the latest server-side details - status,
      signer counts, current step, payment state.
    </p>

    <h3>Get Signer Form Field Entries</h3>
    <p>
      Returns the values a signer typed into the document's form fields (text
      fields, checkboxes, dropdowns, dates). One Zapier item per filled field.
      Pipe into Salesforce / HubSpot / Airtable / Google Sheets to sync
      collected form data downstream of a completed signing - closes the
      canonical "package completed &rarr; write fields back to CRM" Zap with
      no custom-code step required.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Common Zap recipes</h2>

    <div class="guide-example">
      <strong>"Save signed contracts to Google Drive when complete."</strong><br>
      <em>Trigger:</em> Package Completed &rarr;
      <em>Action:</em> Download Signed Documents (ZIP) &rarr;
      <em>Action:</em> Google Drive: Upload File
    </div>

    <div class="guide-example">
      <strong>"Post a Slack alert when a contract is sent."</strong><br>
      <em>Trigger:</em> Package Sent &rarr;
      <em>Action:</em> Slack: Send Channel Message (with package name +
      external reference ID + sent-at timestamp)
    </div>

    <div class="guide-example">
      <strong>"Add a Google Sheets row for each completed signer."</strong><br>
      <em>Trigger:</em> Signer Completed &rarr;
      <em>Search:</em> Get Signer Form Field Entries &rarr;
      <em>Action:</em> Google Sheets: Create Spreadsheet Row (one row per
      filled field, or one row per signer with all fields as columns)
    </div>

    <div class="guide-example">
      <strong>"Send a download link via SMS when a contract is completed."</strong><br>
      <em>Trigger:</em> Package Completed &rarr;
      <em>Action:</em> Send a Download Link (Scope: Evidence Pack, recipient
      email = customer's email) &rarr;
      <em>Action:</em> Twilio: Send SMS (body includes the share URL)
    </div>

    <div class="guide-example">
      <strong>"Alert ops when a signer fails KBA."</strong><br>
      <em>Trigger:</em> Signer Authentication Failed &rarr;
      <em>Filter:</em> only continue if isTerminal is true &rarr;
      <em>Action:</em> Slack / Email: notify the deal owner with signer
      details and package GUID so they can reach out manually.
    </div>

    <div class="guide-example" v-pre>
      <strong>"Void the package if the upstream CRM deal closes lost."</strong><br>
      <em>Trigger:</em> CRM: Deal Status Changed &rarr;
      <em>Filter:</em> only continue if status is Closed Lost &rarr;
      <em>Search:</em> Find Package (by external reference ID =
      <code>{{trigger.dealId}}</code>) &rarr;
      <em>Action:</em> Void Package (with reason "CRM deal closed lost")
    </div>

    <div class="guide-example">
      <strong>"Archive completed contracts + audit trail weekly."</strong><br>
      <em>Trigger:</em> Schedule by Zapier (Every Friday) &rarr;
      <em>Search:</em> Find Package (status = Completed, updatedSince = last
      week) &rarr; loop &rarr;
      <em>Action:</em> Download Audit Trail &rarr;
      <em>Action:</em> SharePoint / S3: Upload File
    </div>

    <q-separator class="q-my-lg" />

    <h2>Choosing the right action for "send the artifact somewhere"</h2>
    <p>
      Two actions can deliver a signed package to a recipient. Picking the
      right one matters for both file-size limits and security posture.
    </p>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Destination</th>
          <th>Use this action</th>
          <th>Why</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Email, Slack, SMS, CRM note, Notion, Airtable - anything taking a URL</td>
          <td><strong>Send a Download Link</strong></td>
          <td>Small URL, forwards safely (each recipient still has to verify
          their email), tenant-revocable. Avoids the file-size caps most
          messaging destinations enforce (10–25 MB).</td>
        </tr>
        <tr>
          <td>Cloud-storage upload (Drive, Dropbox, OneDrive, SharePoint, Box, S3)</td>
          <td><strong>Download Signed Documents (ZIP)</strong></td>
          <td>Pipes the bytes into the storage destination's "Upload File"
          action.</td>
        </tr>
        <tr>
          <td>Long-term compliance archive</td>
          <td><strong>Download Audit Trail</strong> + <strong>Download Signed Documents</strong></td>
          <td>Preserves the cryptographic chain for independent re-verification.</td>
        </tr>
        <tr>
          <td>Both messaging + storage</td>
          <td>Two action steps in the same Zap</td>
          <td>e.g. archive to Drive AND email a download link to the client.</td>
        </tr>
      </tbody>
    </table>
    <p>
      <strong>Why the OTP gate matters.</strong> The Send-Download-Link path
      requires the recipient to enter a 6-digit code from their email before
      the file unlocks. That's a deliberate posture, not a UX choice - it
      means a forwarded link is still safe (the new holder also has to control
      the bound inbox), the download is auditable per recipient, and a tenant
      can revoke a link if it leaks.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What only we have</h2>
    <p>
      Side-by-side with DocuSign, Adobe Acrobat Sign, Dropbox Sign, SignNow,
      and PandaDoc on Zapier, three eSign Launchpad capabilities have no
      direct equivalent on competitor connectors:
    </p>
    <ul>
      <li>
        <strong>Share Link Redeemed trigger</strong> closes the loop on
        artifact delivery - fires when a recipient OTP's through a share link
        and downloads. Combined with <em>Send a Download Link</em>, you get a
        verified-receipt audit trail without integrating a separate email-
        tracking service.
      </li>
      <li>
        <strong>Universal authentication-pass triggers</strong> fire on KBA,
        IDV, SMS OTP, and email OTP success. DocuSign exposes IDV-pass
        triggers but only on premium QES tier; we surface them on every plan
        that supports the auth method.
      </li>
      <li>
        <strong>Form-field extraction search</strong> returns each filled
        field as a separate Zapier item - drop directly into a CRM-create
        action without a custom-code step. Adobe Acrobat Sign has a similar
        search; nobody else does.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Limits and good practice</h2>
    <ul>
      <li>
        <strong>Webhook delivery rate limits.</strong> Each tenant can have
        up to 50 webhook subscriptions. There is no per-tenant rate limit on
        webhook deliveries themselves, but Zapier itself rate-limits inbound
        webhooks per Zap.
      </li>
      <li>
        <strong>Per-action server-side rate limits.</strong> Resend
        Notification is capped at 5 resends per signer per minute. Audit
        trail export is capped at 60 exports per tenant per hour. The
        standard 120 requests-per-minute API limit applies to all other
        actions.
      </li>
      <li>
        <strong>Re-authentication on scope addition.</strong> When a future
        version adds a new scope, existing connections won't have it; Zaps
        using the new capability will prompt for re-auth on first run. The
        old scope set keeps working for existing Zaps.
      </li>
      <li>
        <strong>External reference IDs are your friend.</strong> Stamp your
        upstream system's ID on the package at create time
        (<code>externalReferenceId</code>). Every trigger payload echoes it
        back, and <em>Find Package</em> can search by it. This is the cleanest
        way to round-trip between eSign Launchpad and the CRM/form-builder
        that originated the signing request.
      </li>
      <li>
        <strong>Test with disposable email addresses.</strong> Patterns like
        <code>your-name+test1@yourdomain.com</code> let you iterate on Zap
        configuration without emailing real signers. The plus-suffix routes
        to the same inbox in most providers.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Where to go next</h2>
    <ul>
      <li>
        <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> - the
        Zapier triggers are built on top of our webhook delivery
        infrastructure. The webhooks guide covers payload schemas, signature
        verification, and retry behavior.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>
        - full scope catalog if you ever need to grant or revoke specific
        capabilities.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>
        - required reading before using the <em>Get URL for Embedded Signer</em>
        action.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=ai-agents">AI Agents</a> - if
        Zapier is too rigid for your use case, the MCP server lets AI
        assistants drive eSign Launchpad in plain language with full API
        access.
      </li>
      <li>
        <a href="/Docs/ApiReference">API Reference</a> - every Zapier action is
        a thin wrapper around a REST endpoint documented here.
      </li>
    </ul>
  </div>
</template>

<script>
export default {
    name: 'GuideZapier'
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}

.guide-example {
    margin: 8px 0;
    padding: 12px 14px;
    background: #f8fafc;
    border-left: 3px solid #6366F1;
    border-radius: 0 6px 6px 0;
    color: #334155;
    line-height: 1.55;
}

.guide-example strong {
    display: block;
    margin-bottom: 4px;
    color: #1e293b;
}

.guide-table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
}

.guide-table th,
.guide-table td {
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
    line-height: 1.5;
}

.guide-table th {
    background: #f8fafc;
    color: #334155;
    font-weight: 600;
}
</style>
